import { config } from "@/config/environment";

/**
 * PWA Update Manager
 * Handles intelligent update detection to prevent false positives for first-time users
 */
export class PWAUpdateManager {
  private static instance: PWAUpdateManager;
  private isFirstTimeVisit: boolean;
  private initialRegistrationComplete: boolean = false;
  private registrationTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.isFirstTimeVisit = !localStorage.getItem("pwa-has-visited");
    if (this.isFirstTimeVisit) {
      localStorage.setItem("pwa-has-visited", "true");
    }
  }

  public static getInstance(): PWAUpdateManager {
    if (!PWAUpdateManager.instance) {
      PWAUpdateManager.instance = new PWAUpdateManager();
    }
    return PWAUpdateManager.instance;
  }

  /**
   * Mark that initial service worker registration is complete
   */
  public markInitialRegistrationComplete(): void {
    this.initialRegistrationComplete = true;
    if (this.registrationTimer) {
      clearTimeout(this.registrationTimer);
      this.registrationTimer = null;
    }

    if (config.debug.enabled) {
      console.log("PWA initial registration marked as complete");
    }
  }

  /**
   * Start the initial registration timer
   * This prevents false update prompts during first-time registration
   */
  public startInitialRegistrationTimer(): void {
    if (this.registrationTimer) {
      clearTimeout(this.registrationTimer);
    }

    this.registrationTimer = setTimeout(() => {
      this.markInitialRegistrationComplete();
    }, 2000); // 2 second delay
  }

  /**
   * Check if an update should be shown to the user
   */
  public shouldShowUpdate(): boolean {
    // Don't show updates during initial registration
    if (!this.initialRegistrationComplete) {
      if (config.debug.enabled) {
        console.log("PWA update blocked - initial registration not complete");
      }
      return false;
    }

    // Don't show updates for first-time visitors
    if (this.isFirstTimeVisit) {
      if (config.debug.enabled) {
        console.log("PWA update blocked - first-time visitor");
      }
      return false;
    }

    return true;
  }

  /**
   * Handle when an update is detected
   */
  public onUpdateDetected(): boolean {
    const shouldShow = this.shouldShowUpdate();

    if (shouldShow) {
      if (config.debug.enabled) {
        console.log("PWA update approved for returning user");
      }
    } else {
      if (config.debug.enabled) {
        console.log(
          "PWA update blocked for first-time user or during initial registration"
        );
      }
    }

    return shouldShow;
  }

  /**
   * Get the current state
   */
  public getState() {
    return {
      isFirstTimeVisit: this.isFirstTimeVisit,
      initialRegistrationComplete: this.initialRegistrationComplete
    };
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    if (this.registrationTimer) {
      clearTimeout(this.registrationTimer);
      this.registrationTimer = null;
    }
  }
}

// Export singleton instance
export const pwaUpdateManager = PWAUpdateManager.getInstance();
